import { useEffect, useRef, useState } from "react"
import {
  CALORIES_PER_CARBS,
  CALORIES_PER_FAT,
  CALORIES_PER_PROTEIN,
  getCaloriesFromMacrosGram,
  getCaloriesPercentFromMacrosGram,
  getMarcoGramFromCaloriesPercent,
} from "features/profile/hooks/manualEditGoal/helper"
import { FoodType, MealType } from "model/food/types"
import { roundMealMacro, roundCalories } from "utils"
import { IAIScanFoodOption } from "model/food/types/AIScan.types"
import { MEAL_MACRO_DECIMAL_PLACE } from "config"

export interface IMarcroPercentAndGram {
  proteinCaloriesPercent: number
  fatCaloriesPercent: number
  carbsCaloriesPercent: number
  proteinGram: null | number
  fatGram: null | number
  carbsGram: null | number
}

const useManualEditNutritionForm = (
  meal: MealType | FoodType | IAIScanFoodOption,
) => {
  const initValues = useRef({
    initCalories: 0,
    initProteinGram: 0,
    initFatGram: 0,
    initCarbsGram: 0,
  })

  useEffect(() => {
    const { calorie, carbohydrate, protein, fat } = meal
    const roundedProteinGram = roundMealMacro(protein)
    const roundedFatGram = roundMealMacro(fat)
    const roundedCarbsGram = roundMealMacro(carbohydrate)
    const roundedCalories = roundCalories(calorie)

    initValues.current = {
      initCalories: roundedCalories,
      initProteinGram: roundedProteinGram,
      initFatGram: roundedFatGram,
      initCarbsGram: roundedCarbsGram,
    }

    setCalories(roundedCalories)
    setProteinGram(roundedProteinGram)
    setFatGram(roundedFatGram)
    setCarbsGram(roundedCarbsGram)

    updateCaloriesPercentWhenMacrosGramChange({
      proteinGram: roundedProteinGram,
      fatGram: roundedFatGram,
      carbsGram: roundedCarbsGram,
    })
  }, [])

  const [calories, setCalories] = useState<null | number>(null)
  const [isCaloriesError, setIsCaloriesError] = useState(false)

  useEffect(() => {
    const updateCaloriesError = (value: null | number) => {
      if (value === null || value <= 0 || value >= 10000) {
        setIsCaloriesError(true)
      } else {
        setIsCaloriesError(false)
      }
    }

    updateCaloriesError(calories)
  }, [calories])

  const updateMacroGramsFromCalories = (calories: null | number) => {
    const nextProteinGram = getMarcoGramFromCaloriesPercent({
      calories,
      percent: proteinCaloriesPercent,
      macroCaloriesPerGram: CALORIES_PER_PROTEIN,
      decimalPlaces: MEAL_MACRO_DECIMAL_PLACE,
    })

    const nextFatGram = getMarcoGramFromCaloriesPercent({
      calories,
      percent: fatCaloriesPercent,
      macroCaloriesPerGram: CALORIES_PER_FAT,
      decimalPlaces: MEAL_MACRO_DECIMAL_PLACE,
    })
    const nextCarbsGram = getMarcoGramFromCaloriesPercent({
      calories,
      percent: carbsCaloriesPercent,
      macroCaloriesPerGram: CALORIES_PER_CARBS,
      decimalPlaces: MEAL_MACRO_DECIMAL_PLACE,
    })

    setProteinGram(nextProteinGram)
    setFatGram(nextFatGram)
    setCarbsGram(nextCarbsGram)
  }

  const onCaloriesChange = (nextCalories: null | number) => {
    setCalories(nextCalories)
    updateMacroGramsFromCalories(nextCalories)
  }

  const updateCaloriesWhenMacrosGramChange = ({
    proteinGram,
    fatGram,
    carbsGram,
  }: {
    proteinGram: null | number
    fatGram: null | number
    carbsGram: null | number
  }) => {
    const caloriesFromMacros = getCaloriesFromMacrosGram({
      proteinGram,
      fatGram,
      carbsGram,
    })
    setCalories(caloriesFromMacros)
    updateCaloriesPercentWhenMacrosGramChange({
      proteinGram,
      fatGram,
      carbsGram,
    })
  }

  const updateCaloriesPercentWhenMacrosGramChange = ({
    proteinGram,
    fatGram,
    carbsGram,
  }: {
    proteinGram: null | number
    fatGram: null | number
    carbsGram: null | number
  }) => {
    const { proteinPercent, fatPercent, carbPercent } =
      getCaloriesPercentFromMacrosGram({
        proteinGram,
        fatGram,
        carbsGram,
      })
    setProteinCaloriesPercent(proteinPercent)
    setFatCaloriesPercent(fatPercent)
    setCarbsCaloriesPercent(carbPercent)
  }

  const [proteinGram, setProteinGram] = useState<null | number>(null)
  const [proteinCaloriesPercent, setProteinCaloriesPercent] = useState(
    Math.round(meal.protein * 100),
  )
  const [isProteinGramError, setIsProteinGramError] = useState(false)

  const onProteinGramChange = (value: null | number) => {
    setProteinGram(value)
    setIsProteinGramError(value === null ? true : false)

    updateCaloriesWhenMacrosGramChange({
      proteinGram: value,
      fatGram,
      carbsGram,
    })

    updateCaloriesPercentWhenMacrosGramChange({
      proteinGram: value,
      fatGram,
      carbsGram,
    })
  }

  const [fatGram, setFatGram] = useState<null | number>(null)
  const [fatCaloriesPercent, setFatCaloriesPercent] = useState(
    Math.round(meal.fat * 100),
  )
  const [isFatGramError, setIsFatGramError] = useState(false)
  const onFatGramChange = (value: null | number) => {
    setFatGram(value)
    setIsFatGramError(value === null ? true : false)

    updateCaloriesWhenMacrosGramChange({
      proteinGram,
      fatGram: value,
      carbsGram,
    })

    updateCaloriesPercentWhenMacrosGramChange({
      proteinGram,
      fatGram: value,
      carbsGram,
    })
  }

  const [carbsGram, setCarbsGram] = useState<null | number>(null)
  const [isCarbsGramError, setIsCarbsGramError] = useState(false)

  const [carbsCaloriesPercent, setCarbsCaloriesPercent] = useState(
    Math.round(meal.carbohydrate * 100),
  )
  const onCarbsGramChange = (value: null | number) => {
    setCarbsGram(value)
    setIsCarbsGramError(value === null ? true : false)

    updateCaloriesWhenMacrosGramChange({
      proteinGram,
      fatGram,
      carbsGram: value,
    })

    updateCaloriesPercentWhenMacrosGramChange({
      proteinGram,
      fatGram,
      carbsGram: value,
    })
  }

  useEffect(() => {
    const hasMacroPercents =
      proteinCaloriesPercent + fatCaloriesPercent + carbsCaloriesPercent > 0

    if (!hasMacroPercents) {
      setProteinCaloriesPercent(100)
    }
  }, [proteinCaloriesPercent, fatCaloriesPercent, carbsCaloriesPercent])

  const isValuesChanged = () => {
    const { initCalories, initCarbsGram, initFatGram, initProteinGram } =
      initValues.current
    return (
      initCalories !== calories ||
      initCarbsGram !== carbsGram ||
      initFatGram !== fatGram ||
      initProteinGram !== proteinGram
    )
  }

  return {
    calories,
    proteinGram,
    fatGram,
    carbsGram,
    isCaloriesError,
    isProteinGramError,
    isFatGramError,
    isCarbsGramError,
    onCaloriesChange,
    onProteinGramChange,
    onFatGramChange,
    onCarbsGramChange,
    isValuesChanged,
  }
}

export default useManualEditNutritionForm
